/**
 * Tuition Service
 * 
 * Handles all tuition-related API calls.
 */

import api from '../utils/api';

/**
 * Get all tuitions
 * @param {Object} params - Query parameters (skip, limit, status, student_id, etc.)
 * @returns {Promise} - Get tuitions response
 */
export const getTuitions = async (params = {}) => {
  try {
    // Build query parameters
    const queryParams = {};
    if (params.skip !== undefined) queryParams.skip = params.skip;
    if (params.limit !== undefined) queryParams.limit = params.limit;
    if (params.status) queryParams.status_filter = params.status;
    if (params.status_filter) queryParams.status_filter = params.status_filter;
    if (params.student_id) queryParams.student_id = params.student_id;
    if (params.type) queryParams.type = params.type;
    if (params.center) queryParams.center = params.center;
    if (params.prepa_session) queryParams.prepa_session = params.prepa_session;
    if (params.search) queryParams.search = params.search;

    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/v1/tuitions/${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(endpoint);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        pagination: response.pagination,
        message: response.message || 'Tuitions retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve tuitions',
      data: null,
      pagination: null,
    };
  } catch (error) {
    console.error('Get tuitions error:', error);
    
    let errorMessage = 'Failed to retrieve tuitions. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
      pagination: null,
    };
  }
};

/**
 * Reset overdue tuitions to pending
 * @returns {Promise} - Bulk reset response
 */
export const resetOverdueToPending = async () => {
  try {
    const response = await api.post('/v1/tuitions/reset-overdue-to-pending');

    if (response.status === 'success') {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Overdue tuitions reset successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to reset overdue tuitions',
      data: null,
    };
  } catch (error) {
    console.error('Reset overdue tuitions error:', error);

    let errorMessage = 'Failed to reset overdue tuitions. Please try again.';

    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Get a single tuition by ID
 * @param {string} tuitionId - Tuition ID
 * @returns {Promise} - Get tuition response
 */
export const getTuition = async (tuitionId) => {
  try {
    const response = await api.get(`/v1/tuitions/${tuitionId}`);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Tuition retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve tuition',
      data: null,
    };
  } catch (error) {
    console.error('Get tuition error:', error);
    
    let errorMessage = 'Failed to retrieve tuition. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Update a tuition (full update using PUT)
 * @param {string} tuitionId - Tuition ID
 * @param {Object} tuitionData - Tuition data to update
 * @returns {Promise} - Update tuition response
 */
export const updateTuition = async (tuitionId, tuitionData) => {
  try {
    const response = await api.put(`/v1/tuitions/${tuitionId}`, tuitionData);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Tuition updated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to update tuition',
      data: null,
    };
  } catch (error) {
    console.error('Update tuition error:', error);
    
    let errorMessage = 'Failed to update tuition. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Partially update a tuition (PATCH)
 * @param {string} tuitionId - Tuition ID
 * @param {Object} tuitionData - Fields to update (only include fields to update)
 * @returns {Promise} - Update tuition response
 */
export const updateTuitionPartial = async (tuitionId, tuitionData) => {
  try {
    const response = await api.patch(`/v1/tuitions/${tuitionId}`, tuitionData);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Tuition updated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to update tuition',
      data: null,
    };
  } catch (error) {
    console.error('Update tuition partial error:', error);
    
    let errorMessage = 'Failed to update tuition. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Delete a tuition
 * @param {string} tuitionId - Tuition ID
 * @returns {Promise} - Delete tuition response
 */
export const deleteTuition = async (tuitionId) => {
  try {
    const response = await api.delete(`/v1/tuitions/${tuitionId}`);

    if (response.status === 'success') {
      return {
        success: true,
        message: response.message || 'Tuition deleted successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to delete tuition',
    };
  } catch (error) {
    console.error('Delete tuition error:', error);
    
    let errorMessage = 'Failed to delete tuition. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Get payments for tuitions
 * @param {Object} params - Query parameters (skip, limit, tuition_id, status, payment_method)
 * @returns {Promise} - Get payments response
 */
export const getPayments = async (params = {}) => {
  try {
    // Build query parameters
    const queryParams = {};
    if (params.skip !== undefined) queryParams.skip = params.skip;
    if (params.limit !== undefined) queryParams.limit = params.limit;
    if (params.tuition_id) queryParams.tuition_id = params.tuition_id;
    if (params.status) queryParams.status = params.status;
    if (params.payment_method) queryParams.payment_method = params.payment_method;
    if (params.center) queryParams.center = params.center;

    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/v1/tuitions/payments${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(endpoint);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        pagination: response.pagination,
        message: response.message || 'Payments retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve payments',
      data: null,
      pagination: null,
    };
  } catch (error) {
    console.error('Get payments error:', error);
    
    let errorMessage = 'Failed to retrieve payments. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
      pagination: null,
    };
  }
};

/**
 * Get a single payment by ID
 * @param {string} paymentId - Payment ID
 * @returns {Promise} - Get payment response
 */
export const getPayment = async (paymentId) => {
  try {
    const response = await api.get(`/v1/tuitions/payments/${paymentId}`);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Payment retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve payment',
      data: null,
    };
  } catch (error) {
    console.error('Get payment error:', error);
    
    let errorMessage = 'Failed to retrieve payment. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Update a payment (full update using PUT)
 * @param {string} paymentId - Payment ID
 * @param {Object} paymentData - Payment data to update
 * @returns {Promise} - Update payment response
 */
export const updatePayment = async (paymentId, paymentData) => {
  try {
    const response = await api.put(`/v1/tuitions/payments/${paymentId}`, paymentData);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Payment updated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to update payment',
      data: null,
    };
  } catch (error) {
    console.error('Update payment error:', error);
    
    let errorMessage = 'Failed to update payment. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Partially update a payment (PATCH)
 * @param {string} paymentId - Payment ID
 * @param {Object} paymentData - Fields to update (only include fields to update)
 * @returns {Promise} - Update payment response
 */
export const updatePaymentPartial = async (paymentId, paymentData) => {
  try {
    const response = await api.patch(`/v1/tuitions/payments/${paymentId}`, paymentData);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Payment updated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to update payment',
      data: null,
    };
  } catch (error) {
    console.error('Update payment partial error:', error);
    
    let errorMessage = 'Failed to update payment. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Delete a payment
 * @param {string} paymentId - Payment ID
 * @returns {Promise} - Delete payment response
 */
export const deletePayment = async (paymentId) => {
  try {
    const response = await api.delete(`/v1/tuitions/payments/${paymentId}`);

    if (response.status === 'success') {
      return {
        success: true,
        message: response.message || 'Payment deleted successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to delete payment',
    };
  } catch (error) {
    console.error('Delete payment error:', error);
    
    let errorMessage = 'Failed to delete payment. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Validate a payment
 * @param {string} paymentId - Payment ID
 * @returns {Promise} - Validate payment response
 */
export const validatePayment = async (paymentId, validationData = {}) => {
  try {
    const response = await api.post(`/v1/tuitions/payments/${paymentId}/validate`, {
      status: validationData.status || 'validated',
      notes: validationData.notes || undefined,
    });

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Payment validated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to validate payment',
      data: null,
    };
  } catch (error) {
    console.error('Validate payment error:', error);
    
    let errorMessage = 'Failed to validate payment. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Create a payment for a tuition
 * @param {Object} paymentData - Payment data (tuition_id, amount, payment_date, payment_method, reference_number, notes, status)
 * @returns {Promise} - Create payment response
 */
export const createPayment = async (paymentData) => {
  try {
    const response = await api.post('/v1/tuitions/payments', paymentData);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Payment created successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to create payment',
      data: null,
    };
  } catch (error) {
    console.error('Create payment error:', error);
    
    let errorMessage = 'Failed to create payment. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

const tuitionService = {
  getTuitions,
  getTuition,
  updateTuition,
  updateTuitionPartial,
  deleteTuition,
  resetOverdueToPending,
  getPayments,
  getPayment,
  createPayment,
  updatePayment,
  updatePaymentPartial,
  deletePayment,
  validatePayment,
};
export default tuitionService;
